import { Spacer } from "@/components/Spacer";
import Section0201Desktop from "@/assets/images/section-02-01-desktop-eng.png";
import Section0202Desktop from "@/assets/images/section-02-02-desktop-eng.png";
import Section0201Mobile from "@/assets/images/section-02-01-mobile-eng.png";
import Section0202Mobile from "@/assets/images/section-02-02-mobile-eng.png";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

export default function Section02({ isMobile }: { isMobile: boolean }) {
  const sectionRef = useRef(null);
  const section02Ref = useRef(null);
  const isInView = useInView(sectionRef, {
    once: true,
    margin: "0px 0px -50% 0px",
  });
  const isSection02InView = useInView(section02Ref, {
    once: true,
    margin: "0px 0px -50% 0px",
  });

  return (
    <div className="flex flex-col items-center desktop:py-[100px] desktop:px-[32px] py-[80px] px-[24px] bg-[#ffffff]">
      <motion.div
        id="section-02-01"
        className="flex flex-col items-center"
        ref={sectionRef}
        initial={{
          opacity: 0,
          scale: 1,
          rotate: 0,
          skewX: 0,
          skewY: 0,
          y: 20,
          x: 0,
        }}
        animate={
          isInView
            ? {
                opacity: 1,
                y: 0,
                transition: {
                  type: "spring",
                  stiffness: 300,
                  damping: 80,
                  mass: 1,
                  delay: 0.35,
                },
              }
            : {
                opacity: 0,
                y: 20,
                transition: {
                  type: "spring",
                  stiffness: 300,
                  damping: 80,
                  mass: 1,
                  delay: 0.35,
                },
              }
        }
      >
        <div
          className="font-eng-01-semibold desktop:text-[18px] text-[13px] leading-[100%] text-[#868686] text-center"
          style={{ letterSpacing: "-0.02em" }}
        >
          Too busy for a hospital visit?{" "}
          <span className="desktop:hidden block">{"\n"}</span>Unsure what to
          search?
        </div>
        <Spacer height={isMobile ? 8 : 12} />
        <div
          className="font-eng-01-bold desktop:text-[38px] text-[24px] leading-[130%] text-[#151515] text-center"
          style={{ letterSpacing: "-0.03em" }}
        >
          Healthier is with you,
          <span className="desktop:hidden block">{"\n"}</span> anytime, anywhere
        </div>
      </motion.div>
      <Spacer height={isMobile ? 40 : 60} />
      <motion.div
        id="section-02-02"
        className="flex desktop:flex-row flex-col gap-[24px]"
        ref={section02Ref}
        initial={{
          opacity: 0,
          scale: 1,
          rotate: 0,
          skewX: 0,
          skewY: 0,
          y: 30,
          x: 0,
        }}
        animate={
          isSection02InView
            ? {
                opacity: 1,
                y: 0,
                transition: {
                  type: "spring",
                  stiffness: 300,
                  damping: 100,
                  mass: 1,
                  delay: 0.2,
                },
              }
            : {
                opacity: 0,
                y: 30,
                transition: {
                  type: "spring",
                  stiffness: 300,
                  damping: 100,
                  mass: 1,
                  delay: 0.2,
                },
              }
        }
      >
        <Card01 isMobile={isMobile} />
        <Card02 isMobile={isMobile} />
      </motion.div>
    </div>
  );
}

const Card01 = ({ isMobile }: { isMobile: boolean }) => {
  return (
    <div className="desktop:w-[468px] desktop:h-[500px] w-[342px] bg-[#F5F6FA] rounded-[20px] desktop:pt-[50px] pt-[32px] flex flex-col justify-between">
      <div className="w-full desktop:px-[40px] px-[24px] desktop:mb-[0px] mb-[28px]">
        <div
          className="font-eng-semibold desktop:text-[20px] text-[18px] leading-[130%] text-[#131416] whitespace-pre-line"
          style={{ letterSpacing: -0.6 }}
        >
          Curious about your symptoms? {"\n"} Check with Healthier first.
        </div>
        <Spacer height={isMobile ? 8 : 16} />
        <div
          className="font-eng-regular desktop:text-[15px] text-[13px] leading-[130%] text-[#23272E]"
          style={{ letterSpacing: -0.4 }}
        >
          Healthier’s Doctor AI analyzes your symptoms for
          <span className="desktop:hidden block">{"\n"}</span> free.
          <span className="desktop:block hidden">{"\n"}</span> With just a few
          clicks, you can see possible conditions and their severity.
        </div>
      </div>
      <img
        src={isMobile ? Section0201Mobile : Section0201Desktop}
        alt="Curious about your symptoms? Check with Healthier first."
        className=" w-full desktop:h-[290px] h-[195px]"
      />
    </div>
  );
};

const Card02 = ({ isMobile }: { isMobile: boolean }) => {
  return (
    <div className="desktop:w-[468px] desktop:h-[500px] w-[342px] bg-[#F5F6FA] rounded-[20px] desktop:pt-[50px] pt-[32px] flex flex-col justify-between">
      <div className="w-full desktop:pl-[40px] pl-[24px] desktop:mb-[0px] mb-[28px]">
        <div
          className="font-eng-semibold desktop:text-[20px] text-[18px] leading-[130%] text-[#131416] whitespace-pre-line"
          style={{ letterSpacing: -0.6 }}
        >
          Get info on potential illnesses,{"\n"} recommended medications, and
          nearby hospitals — all in one place.
        </div>
        <Spacer height={isMobile ? 8 : 16} />
        <div
          className="font-eng-regular desktop:text-[15px] text-[13px] leading-[130%] text-[#23272E] "
          style={{ letterSpacing: -0.4 }}
        >
          Quickly and easily find detailed information about your
          <span className="desktop:block hidden">{"\n"}</span>
          condition and the right medical department
          <span className="dekstop:hidden block">{"\n"}</span> for your
          symptoms.
        </div>
      </div>
      <img
        src={isMobile ? Section0202Mobile : Section0202Desktop}
        alt="Curious about your symptoms? Check with Healthier first."
        className=" w-full desktop:h-[290px] h-[195px]"
      />
    </div>
  );
};
